import { useState, useEffect, useCallback } from 'react';
import { Card, Row, Col, Statistic, Spin, Alert, DatePicker, Select, Space } from 'antd';
import { useTranslation } from 'react-i18next';
import ReactECharts from 'echarts-for-react';
import {
  getCampaignOverview,
  getCampaignTrend,
  type CampaignOverviewResponse,
  type CampaignTrendResponse,
} from '@/api/marketingApi';

const { RangePicker } = DatePicker;

export default function CampaignAnalyticsDashboard() {
  const { t } = useTranslation();

  const [overview, setOverview] = useState<CampaignOverviewResponse | null>(null);
  const [trend, setTrend] = useState<CampaignTrendResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<string | undefined>();
  const [dateRange, setDateRange] = useState<[string, string] | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string | undefined> = {
        type: typeFilter,
        date_from: dateRange?.[0],
        date_to: dateRange?.[1],
      };
      const [overviewData, trendData] = await Promise.all([
        getCampaignOverview(params),
        getCampaignTrend(params),
      ]);
      setOverview(overviewData);
      setTrend(trendData);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('app.error'));
    } finally {
      setLoading(false);
    }
  }, [typeFilter, dateRange, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) {
    return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;
  }

  if (error) {
    return <Alert type="error" message={error} />;
  }

  // Status pie chart
  const statusPieOption = {
    title: { text: t('campaigns.analytics.by_status'), left: 'center' },
    tooltip: { trigger: 'item' },
    series: [
      {
        type: 'pie',
        radius: '60%',
        data:
          overview?.status_breakdown.map((b) => ({
            name: t(`campaigns.status_options.${b.key}`, b.key),
            value: b.doc_count,
          })) ?? [],
        emphasis: {
          itemStyle: { shadowBlur: 10, shadowOffsetX: 0, shadowColor: 'rgba(0,0,0,0.5)' },
        },
      },
    ],
  };

  // Type pie chart
  const typePieOption = {
    title: { text: t('campaigns.analytics.by_type'), left: 'center' },
    tooltip: { trigger: 'item' },
    series: [
      {
        type: 'pie',
        radius: '60%',
        data:
          overview?.type_breakdown.map((b) => ({
            name: t(`campaigns.type_options.${b.key}`, b.key),
            value: b.doc_count,
          })) ?? [],
        emphasis: {
          itemStyle: { shadowBlur: 10, shadowOffsetX: 0, shadowColor: 'rgba(0,0,0,0.5)' },
        },
      },
    ],
  };

  // Monthly trend line chart
  const trendLineOption = {
    title: { text: t('campaigns.analytics.monthly_trend'), left: 'center' },
    tooltip: { trigger: 'axis' },
    xAxis: {
      type: 'category' as const,
      data: trend?.monthly_trend.map((b) => b.date) ?? [],
    },
    yAxis: { type: 'value' as const },
    series: [
      {
        type: 'line',
        data: trend?.monthly_trend.map((b) => b.doc_count) ?? [],
        smooth: true,
        areaStyle: { opacity: 0.3 },
      },
    ],
  };

  return (
    <div>
      <h2>{t('campaigns.analytics.title')}</h2>

      <Space wrap style={{ marginBottom: 24 }}>
        <Select
          placeholder={t('campaigns.filter.type')}
          style={{ width: 140 }}
          allowClear
          value={typeFilter}
          onChange={setTypeFilter}
          options={[
            { value: 'email', label: t('campaigns.type_options.email') },
            { value: 'sms', label: t('campaigns.type_options.sms') },
            { value: 'social', label: t('campaigns.type_options.social') },
          ]}
        />
        <RangePicker
          onChange={(_, dateStrings) => {
            if (dateStrings[0] && dateStrings[1]) {
              setDateRange([dateStrings[0], dateStrings[1]]);
            } else {
              setDateRange(null);
            }
          }}
        />
      </Space>

      {/* Summary Cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title={t('campaigns.analytics.total_campaigns')}
              value={overview?.total_campaigns ?? 0}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title={t('campaigns.analytics.active_campaigns')}
              value={
                overview?.status_breakdown.find((b) => b.key === 'active')?.doc_count ?? 0
              }
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title={t('campaigns.analytics.completed_campaigns')}
              value={
                overview?.status_breakdown.find((b) => b.key === 'completed')?.doc_count ?? 0
              }
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title={t('campaigns.analytics.recipient_breakdown')}
              value={
                overview?.recipient_status_breakdown.reduce((sum, b) => sum + b.doc_count, 0) ?? 0
              }
            />
          </Card>
        </Col>
      </Row>

      {/* Charts */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={12}>
          <Card>
            <ReactECharts option={statusPieOption} style={{ height: 350 }} />
          </Card>
        </Col>
        <Col span={12}>
          <Card>
            <ReactECharts option={typePieOption} style={{ height: 350 }} />
          </Card>
        </Col>
      </Row>

      <Card>
        <ReactECharts option={trendLineOption} style={{ height: 350 }} />
      </Card>
    </div>
  );
}
